import {put, call} from 'redux-saga/effects';
import HolidayCalendar from "../../entities/holidayCalendar";
import * as Actions from "../actions/holidayCalendarActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type HolidayCalendarAction = {
    holidayCalendarId: string;
    holidayCalendar: HolidayCalendar;
    holidayCalendars: HolidayCalendar[];
};

export function* fetchOneHolidayCalendar(action: HolidayCalendarAction) {
	try {
		const holidayCalendar: HolidayCalendar = yield call(api.findOne, 'holidayCalendar', action.holidayCalendarId);

		// Now we can update the user interface
		yield put({type: Actions.HOLIDAYCALENDAR_FETCH_ONE_SUCCESS, holidayCalendarId: action.holidayCalendarId, holidayCalendar});
	}
	catch(err) {
		yield put({type: Actions.HOLIDAYCALENDAR_FETCH_ONE_FAILURE, holidayCalendarId: action.holidayCalendarId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {HolidayCalendarAction} action
 */
export function* saveHolidayCalendar(action: HolidayCalendarAction) {
    // console.debug('⮂ HOLIDAYCALENDAR_SAVE saga', action.holidayCalendar?.id);
    
	if (action.holidayCalendar === undefined || action.holidayCalendar === null) {
		console.error('saveHolidayCalendar received a null');
		throw new Error('saveHolidayCalendar received a null');
	}

	if (!(action.holidayCalendar instanceof HolidayCalendar)) {
		action.holidayCalendar = new HolidayCalendar(action.holidayCalendar);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'holidayCalendar', action.holidayCalendar);
		if (addResult.isErr()) {
			console.error(`⮂ saveHolidayCalendar: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.HOLIDAYCALENDAR_SAVE_FAILURE, holidayCalendar: action.holidayCalendar, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.HOLIDAYCALENDAR_SAVE, holidayCalendar: action.holidayCalendar});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'holidayCalendar', action.holidayCalendar.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.HOLIDAYCALENDAR_SAVE_FAILURE, holidayCalendar: action.holidayCalendar, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {HolidayCalendarAction} action
 * @param {HolidayCalendar[]} action.holidayCalendars
 */
export function* saveManyHolidayCalendars(action: HolidayCalendarAction) {
    // console.debug('⮂ HOLIDAYCALENDAR_SAVE_MANY saga', action.holidayCalendars.map(holidayCalendar => holidayCalendar.id).join(','));
    
    if (!Array.isArray(action.holidayCalendars)) {
        console.error('saveManyHolidayCalendars expected an array, received', action.holidayCalendars);
        throw new Error('saveManyHolidayCalendars expected an array');
    }

	try {
		const toSave: HolidayCalendar[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.holidayCalendars.length; i++) {
			const holidayCalendar = action.holidayCalendars[i];
			holidayCalendar.persisted = false;
			toSave.push(holidayCalendar);
		}

		const addResult = yield call(idb.add, 'holidayCalendar', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyHolidayCalendars: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.HOLIDAYCALENDAR_SAVE_MANY_FAILURE, holidayCalendars: action.holidayCalendars, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.HOLIDAYCALENDAR_SAVE_MANY, holidayCalendars: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.HOLIDAYCALENDAR_SAVE_MANY_FAILURE, holidayCalendars: action.holidayCalendars, status: 500, err});

		console.error(`Error saving ${action.holidayCalendars.length} holidayCalendars to database`, err);
	}
}

/**
 * @param {HolidayCalendarAction} action
 */
export function* removeHolidayCalendar(action: HolidayCalendarAction) {
    const userId = getCurrentUserId();
	if (!userId || action.holidayCalendar === undefined || action.holidayCalendar === null || !(action.holidayCalendar instanceof HolidayCalendar)) {
			console.error('removeHolidayCalendar expected type HolidayCalendar, received', action.holidayCalendar);
			throw new Error('removeHolidayCalendar expected type HolidayCalendar');
		}

	try {
		// Mark as deleted
		action.holidayCalendar.status = 'D';
		action.holidayCalendar.addedOn = newHRTimestamp();
		action.holidayCalendar.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'holidayCalendar', action.holidayCalendar);
		if (addResult.isErr()) {
			console.error(`⮂ removeHolidayCalendar: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.HOLIDAYCALENDAR_REMOVE_FAILURE, holidayCalendar: action.holidayCalendar, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.HOLIDAYCALENDAR_REMOVE, holidayCalendar: action.holidayCalendar});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.HOLIDAYCALENDAR_REMOVE_FAILURE, holidayCalendar: action.holidayCalendar, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this HolidayCalendar.
	*
	* @param {HolidayCalendarAction} action
	*/
export function* restoreHolidayCalendar(action: HolidayCalendarAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreHolidayCalendar expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.holidayCalendar === undefined || action.holidayCalendar === null) {
        console.error('restoreHolidayCalendar expected a HolidayCalendar, received null');
        throw new Error('restoreHolidayCalendar expected a HolidayCalendar, received null');
    }
    else if (!(action.holidayCalendar instanceof HolidayCalendar)) action.holidayCalendar = new HolidayCalendar(action.holidayCalendar); 
    
	try {
		// Mark as restored
		action.holidayCalendar.status = 'U';
		action.holidayCalendar.addedOn = newHRTimestamp();
		action.holidayCalendar.addedBy = userId;
		action.holidayCalendar.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'holidayCalendar', action.holidayCalendar);
		if (addResult.isErr()) {
			console.error(`⮂ restoreHolidayCalendar: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.HOLIDAYCALENDAR_RESTORE_FAILURE, holidayCalendar: action.holidayCalendar, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.HOLIDAYCALENDAR_RESTORE, holidayCalendar: action.holidayCalendar});
	}
	catch(err) {
		yield put({type: Actions.HOLIDAYCALENDAR_RESTORE_FAILURE, holidayCalendar: action.holidayCalendar, status: 500, err});

		console.error('Error restoring from database', err);
	}
}